import { useState, useRef, useEffect } from "react";
import ReactPlayer from "react-player";
import { Volume2, VolumeX, Play, ExternalLink, LinkIcon } from "lucide-react";
import { mediaSrc } from "@/lib/img";

const domainOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return ""; }
};

// Autoplaying, mutable embed for links react-player can play (YouTube, Vimeo, direct files).
function PlayableEmbed({ link, onFail }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [mounted] = useState(true);
  const [muted, setMuted] = useState(true);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      setVisible(e.isIntersecting);
    }, { threshold: 0.5 });
    obs.observe(el); return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative w-full bg-black aspect-video" data-testid="link-embed">
      {link.image && !started && (
        <img src={link.image} alt="" decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-60" />
      )}
      {mounted && (
        <ReactPlayer
          src={link.url}
          playing={visible}
          muted={muted}
          loop
          playsInline
          controls={false}
          width="100%"
          height="100%"
          style={{ position: "absolute", inset: 0 }}
          onPlay={() => setStarted(true)}
          onError={() => onFail?.()}
          config={{ youtube: { color: "white" } }}
        />
      )}
      {!started && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-black/55 border border-white/30 flex items-center justify-center">
            <Play size={24} className="text-white translate-x-0.5" fill="white" />
          </div>
        </div>
      )}
      <button onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
        data-testid="link-embed-mute"
        className="absolute bottom-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors">
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
      </button>
      <a href={link.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
        data-testid="link-embed-source"
        className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 rounded-full bg-black/60 border border-white/20 px-3 py-1.5 text-[11px] text-white hover:bg-black/80 transition-colors">
        <ExternalLink size={12} /> {link.site_name || domainOf(link.url)}
      </a>
    </div>
  );
}

// Clickable rich preview for links that can't be embedded/autoplayed (IG, TikTok, X, plain sites).
function PreviewCard({ link }) {
  const isVideo = link.kind === "video";
  return (
    <a href={link.url} target="_blank" rel="noopener noreferrer" data-testid="link-preview-card"
      className="block border-t border-b border-white/10 bg-ink-950 hover:bg-ink-900 transition-colors">
      {link.image ? (
        <div className="relative w-full aspect-video bg-black">
          <img src={link.image} alt="" decoding="async" className="w-full h-full object-cover" />
          {isVideo && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-black/55 border border-white/30 flex items-center justify-center">
                <Play size={24} className="text-white translate-x-0.5" fill="white" />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="w-full aspect-[3/1] bg-gradient-to-br from-ink-800 to-ink-900 flex items-center justify-center">
          <LinkIcon size={28} className="text-zinc-600" />
        </div>
      )}
      <div className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gold/80 mb-1">
          <ExternalLink size={11} /> {link.site_name || domainOf(link.url)}
        </div>
        <div className="font-heading font-bold text-sm leading-snug line-clamp-2">{link.title || link.url}</div>
        {link.description && <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{link.description}</p>}
      </div>
    </a>
  );
}

export default function LinkCard({ link }) {
  const [failed, setFailed] = useState(false);
  if (!link || !link.url) return null;
  const resolved = { ...link, image: link.image ? mediaSrc(link.image) : null };
  if (resolved.playable && !failed) {
    return <PlayableEmbed link={resolved} onFail={() => setFailed(true)} />;
  }
  return <PreviewCard link={resolved} />;
}
